// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import { Link, Typography, withStyles } from '@material-ui/core';
import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux';
import { compose } from 'redux';
import styles from "./styles";
import { fetchApiProxyList } from "../../actions/apiProxy";
import PropTypes, { func, object } from 'prop-types';
import ListContainer from "../list";
import { useIntl } from 'react-intl';
import { getI18n, getI18nFormattedMessage } from '../../utils/intl';
import { push } from 'connected-react-router';
import LensIcon from '@material-ui/icons/Lens';
import {
  PROXY_CONNECTION_STATUS, PROXY_CONNECTION_STATUS_CONNECTED,
  PROXY_CONNECTION_STATUS_NOT_CONNECTED, ALL,
} from '../../constants';
import {
  FilterByKeyStoreName, FilterSeparator, FilterByConnectionStatus,FilterByName,
} from './controls';
import { getApiProxyListResults, getIsLoading } from '../../reducers/apiProxy';
import { getIsPortalAdmin } from '../../reducers/portalConfig';

const ProxyName = ({ classes, item }) => {
  return (
    <Link
      className={classes.proxyNameContainer}
      underline='none'
      href={`/publish/api-proxy/details/${item.uuid}/`}
    >
      {item.name}
    </Link>
  )
}

ProxyName.propTypes = {
  classes: PropTypes.object,
  item: object,
}

const ProxyUrl = ({ classes, item }) => {
  const httpPrefix = 'http://';
  const httpsPrefix = 'https://';
  const defaultPort = '8443';
  const { enrollmentStatus, proxyUrl, clusterNameOverride } = item;
  let url = clusterNameOverride ? clusterNameOverride : proxyUrl;
  if(url){
    if (!url.startsWith(httpsPrefix) && !url.startsWith(httpPrefix)) {
      url = httpsPrefix + url + ":" + defaultPort;
    }
  }

  return (
    enrollmentStatus === "PENDING" ?
      <Typography variant='subtitle2' className={classes.proxyUrlContainer}>
        {getI18nFormattedMessage("label.proxy.list.api.proxy.url.pending.message")}
      </Typography> : url
  )
}

ProxyUrl.propTypes = {
  classes: object,
  item: object,
}

const KeyStoreName = ({ classes, item }) => {
  return (
    <Link
      className={classes.keyStoreNameContainer}
      underline='none'
      href={`/admin/key-stores/details/${item.keyStoreUuid}/`}
    >
      {item.keyStoreName}
    </Link>
  )
}

KeyStoreName.propTypes = {
  classes: object,
  item: object,
}

const ConnectionStatus = ({ classes, item }) => {
  let statusColor = '';
  let statusText = '';
  const { connectionStatus } = item;
  switch (connectionStatus) {
    case PROXY_CONNECTION_STATUS_CONNECTED:
      statusColor = '#3E7C07';
      statusText = getI18nFormattedMessage('label.proxy.list.connection.status.connected');
      break;
    case PROXY_CONNECTION_STATUS_NOT_CONNECTED:
      statusColor = '#B30303';
      statusText = getI18nFormattedMessage('label.proxy.list.connection.status.not.connected')
      break;
  }
  return (
    <Typography>
      <span className={classes.proxyConnectionStatusContainer}>
        <LensIcon style={{ color: statusColor }} className={classes.connectionStatusIcon}/>
        {statusText}
      </span>
    </Typography>
  )
}

ConnectionStatus.propTypes = {
  classes: PropTypes.object,
  item: object,
}

const getProxyListColumns = ({ classes }) => [
  {
    id: 'Name',
    label: getI18nFormattedMessage("label.api.deployments.list.column.name"),
    minWidth: 200,
    value: (item) => <ProxyName classes={classes} item={item} />,
  },
  {
    id: 'ProxyUrl',
    label: getI18nFormattedMessage("label.proxy.details.page.overview.proxy.url"),
    minWidth: 300,
    value: (item) => <ProxyUrl classes={classes} item={item}/>,
  },
  {
    id: "KeyStoreName",
    label: getI18nFormattedMessage("label.key.store"),
    value: (item) => <KeyStoreName classes={classes} item={item} />,
  },
  {
    id: "ConnectionStatus",
    label: getI18nFormattedMessage("label.proxy.list.connection.status"),
    value: (item) => <ConnectionStatus classes={classes} item={item}/>,
  },
];

export const ApiProxyList = (props) => {
  const {
    apiProxyList = [],
    fetchApiProxyList,
    push,
    loading,
    isPortalAdmin,
    classes,
  } = props

  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationStatus, setNotificationStatus] = useState('');
  const [filterByKeyStoreName, setFilterByKeyStorename] = useState('');
  const [filterByName, setFilterByName] = useState('');
  const [filterByConnectionStatus, setFilterByConnectionStatus] = useState(ALL);
  const [appliedNameFilter, setAppliedNameFilter] = useState('');
  const [appliedKeyStoreNameFilter, setAppliedKeyStoreNameFilter] = useState('');
  const [appliedConnectionStatusFilter, setAppliedConnectionStatusFilter] = useState(ALL)

  const intl = getI18n(useIntl());

  useEffect(() => {
    fetchApiProxyList();
  }, [])

  const onAddProxy = () => {
    push('/publish/api-proxy/add/');
  };

  useEffect(() => {
    if(!isPortalAdmin) {
      push('/404');
    }
  }, [isPortalAdmin])

  const isFilterDirty = () =>
    appliedKeyStoreNameFilter !== '' ||
    appliedNameFilter !== '' ||
    appliedConnectionStatusFilter !== "ALL"

  const noResultsMessage = isFilterDirty() ?
    intl.getI18nMessage('label.proxy.filter.no.results') :
    intl.getI18nMessage('label.proxy.no.results');

  const onFilterByNameChange = (value) => {
    setFilterByName(value);
  };

  const onFilterByNameKeyPress = (e) => {
    if(e.key === 'Enter' && filterByName !== appliedNameFilter){
      refetchApiProxiesList();
      setAppliedNameFilter(filterByName);
    }
  };

  const onFilterByKeyStoreNameChange = (value) => {
    setFilterByKeyStorename(value);
  };

  const onFilterByKeyStoreNameKeyPress = (e) => {
    if(e.key === 'Enter' && filterByKeyStoreName !== appliedKeyStoreNameFilter){
      refetchApiProxiesList();
      setAppliedKeyStoreNameFilter(filterByKeyStoreName);
    }
  };

  const onFilterByConnectionStatusChange = (value) => {
    if(appliedConnectionStatusFilter !== value){
      setFilterByConnectionStatus(value)
      refetchApiProxiesList(value);
      setAppliedConnectionStatusFilter(value);
    }
  };

  const refetchApiProxiesList = (connectionStatus = filterByConnectionStatus) => {
    fetchApiProxyList({
      name: filterByName,
      keyStoreName: filterByKeyStoreName,
      connectionStatus:  connectionStatus !== ALL ? connectionStatus : '',
    })
  };

  return (
    <ListContainer
      listPageId='proxy-list-page'
      notificationStatus={notificationStatus}
      setNotificationStatus={setNotificationStatus}
      notificationMessage={notificationMessage}
      setNotificationMessage={setNotificationMessage}
      pageHeaderTitle={intl.getI18nMessage("label.proxy.list.title")}
      addButtonLabel={intl.getI18nMessage("label.proxy.list.add.button.title")}
      rows={apiProxyList}
      onAdd={onAddProxy}
      columns={getProxyListColumns(props)}
      noResultsMessage={noResultsMessage}
      isLoading={loading}
      pageFilterAndSortClass={classes.filtersContainer}
      hasPaginationDisabled={true}
      hidePagination={true}
      filterAndSortContent={(
        <>
          <FilterByName
            name={intl.getI18nMessage('label.filter')}
            value={filterByName}
            fieldContainerClass={classes.fieldContainer}
            placeholder={intl.getI18nMessage("label.api.deployments.list.column.name")}
            handleChange={onFilterByNameChange}
            onKeyPress={onFilterByNameKeyPress}
          />
          <FilterByKeyStoreName
            value={filterByKeyStoreName}
            fieldContainerClass={classes.fieldContainer}
            placeholder={intl.getI18nMessage("label.key.store")}
            handleChange={onFilterByKeyStoreNameChange}
            onKeyPress={onFilterByKeyStoreNameKeyPress}
            hideLabel
          />
          <FilterByConnectionStatus
            value={filterByConnectionStatus}
            data={PROXY_CONNECTION_STATUS}
            selectFieldClass={classes.selectFieldContainer}
            fieldContainerClass={classes.selectFieldContainer}
            handleChange={onFilterByConnectionStatusChange}
            hideLabel
            noNoneOption
            noNativeSelect
          />
          <FilterSeparator />
        </>
      )}
    />
  )
}

const mapStateToProps = (state) => {
  return {
    apiProxyList: getApiProxyListResults(state),
    loading: getIsLoading(state),
    isPortalAdmin: getIsPortalAdmin(state),
  }
}

const mapDispatchToProps = {
  fetchApiProxyList,
  push,
}

ApiProxyList.propTypes = {
  apiProxyList: PropTypes.array,
  fetchApiProxyList: PropTypes.func,
  push: func,
  loading: PropTypes.bool,
  isPortalAdmin: PropTypes.bool,
  classes: object,
}

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(ApiProxyList);
